import { Button, Form, message, Popconfirm, Spin } from 'antd';
import MetricTopInfo from 'emp-3.0/components/Metrics/IndividualMetric/MetricTopInfo';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Icon from '@ant-design/icons';
import CloneIcon from '@icons/emp/clone.svg';
import AddEllipsisWithTooltip from '@components/common/EllipsisTooltip/AddEllipsisWithTooltip';
import { useDeploy, useGetMetricList, useUndeploy } from '@src/emp-3.0/api/queryHooks';
import { IMetric } from '@src/emp-3.0/types';
import { EFormMode } from '@src/emp-3.0/types/common';
import MetricForm from 'emp-3.0/components/Metrics/IndividualMetric/MetricBuilder';
import { cloneDeep } from 'lodash';
import { useCreatePermissions } from '@src/hooks/usePermissions';
import { hasRelatedMetrics, isDeployed } from '@src/emp-3.0/utils/checks';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { ContentWrapper, Divider, Layout, MetricName, Title, TopPanel } from '../../styled';
import {
  ActionConfirmButton,
  getRelatedConfirmTitle,
  renderDeployedStatusDeletePrompt,
  renderDeployPrompt,
  renderUndeployPrompt,
} from '@src/emp-3.0/components/Management/components/CellRenderers/ActionConfirmButton';
import { useMetricBuilderNavigate } from '@src/pages/metric-templates/hooks';
import { useDeleteMetric } from '@src/components/metrics-builder/api/queryHooks';
import EditSvg from '@icons/event-management/edit.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import UndeploySvg from '@icons/emp/undeploy.svg';
import DeploySvg from '@icons/emp/deploy.svg';
import { useMetricBuilderStore } from '@src/emp-3.0/components/Metrics/IndividualMetric/store';
import useScreenSize from '@hooks/useScreenSize';
import { workbenchModeTrueButNotWorkbenchAsset } from '@src/emp-3.0/components/WorkbenchButton/util';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

interface IMetricsBuilderProps {
  onDeleteMetric?: () => void;
  onDeploy?: () => void;
  onUndeploy?: () => void;
}

const MetricsDetail: React.FC<IMetricsBuilderProps> = ({
  onDeleteMetric,
  onDeploy,
  onUndeploy,
}) => {
  const { navigateMetricBuilderIndex, navigateMetricBuilderPage } = useMetricBuilderNavigate();
  const hasCreatePermission = useCreatePermissions();
  const { workbenchMode } = useInitialInfoStore();
  const { screenWidth } = useScreenSize();

  const [searchParams] = useSearchParams();
  const cloneId = searchParams.get('clone_id');
  const { metricId } = useParams<any>();
  const { mutate: deleteMetrics, isLoading: isDeleteLoading } = useDeleteMetric();
  const { data: userDefinedMetrics = [], isFetching: isMetricListLoading } = useGetMetricList();
  const { setFormMode: setWorkflowFormMode } = useWorkflowStore();
  const { setActiveSectionKey, resetAll } = useMetricBuilderStore();
  const { mutate: deployMetric, isLoading: isLoadingDeploy } = useDeploy();
  const { mutate: undeployMetric, isLoading: isLoadingUndeploy } = useUndeploy();
  useEffect(() => {
    resetAll();
    setActiveSectionKey(['1', '2', '3', '4']);
  }, [metricId]);

  useEffect(() => {
    if (!isMetricListLoading && metricId) {
      const metricExists = userDefinedMetrics.find((metric) => metric.id === Number(metricId));
      if (!metricExists) {
        navigateMetricBuilderIndex({ replace: true });
      }
    }
  }, [metricId, isMetricListLoading]);

  const [form] = Form.useForm();
  const metricData = useMemo(() => {
    return cloneDeep(
      userDefinedMetrics.find((x) => x.id === Number(metricId) || x.id === Number(cloneId)),
    ) as IMetric;
  }, [metricId, cloneId, userDefinedMetrics]);

  const isDeployedStatus = isDeployed(metricData);

  const isHasRelatedMetrics = hasRelatedMetrics(metricData);

  const handleDeploy = useCallback(() => {
    if (metricId) {
      deployMetric(
        {
          resource: 'metrics',
          payload: {
            deploy_all: false,
            ids: [Number(metricId)],
          },
        },
        {
          onSuccess: () => {
            message.success('Deploy success!');
            onDeploy?.();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
            message.error(errorMessage);
          },
        },
      );
    }
  }, [deployMetric, metricId, onDeploy]);

  const handleUndeploy = useCallback(() => {
    if (metricId) {
      undeployMetric(
        {
          resource: 'metrics',
          payload: {
            ids: [Number(metricId)],
          },
        },
        {
          onSuccess: () => {
            message.success('Undeploy success!');
            onUndeploy?.();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
            message.error(errorMessage);
          },
        },
      );
    }
  }, [metricId, onUndeploy, undeployMetric]);

  const handleDelete = useCallback(() => {
    deleteMetrics(
      { id: metricId },
      {
        onSuccess: () => {
          message.success('Delete success!');
          onDeleteMetric && onDeleteMetric();
          navigateMetricBuilderIndex({ replace: true });
        },
        onError: (error) => {
          message.error(error?.response?.data?.msg);
        },
      },
    );
  }, [deleteMetrics, metricId, navigateMetricBuilderIndex, onDeleteMetric]);

  const isLoading = isMetricListLoading || isLoadingDeploy || isLoadingUndeploy;

  const deleteButtonProps = useMemo(() => {
    const deleteButtonProps = {
      key: 'metric_detail_delete_button',
      text: 'Delete',
      canAction: !isHasRelatedMetrics,
      onConfirm: handleDelete,
      title: !isHasRelatedMetrics
        ? `Delete ${metricData?.name}?`
        : getRelatedConfirmTitle(metricData, 'delete'),
      button: (
        <Button
          icon={<Icon component={DeleteSvg} />}
          type="primary"
          danger
          ghost
          loading={isDeleteLoading}
          disabled={isLoadingDeploy || isLoadingUndeploy}
          style={{ fontWeight: 500 }}
          size="small"
        >
          Delete
        </Button>
      ),
    };
    if (!isHasRelatedMetrics) {
      Object.assign(deleteButtonProps, {
        icon: <Icon component={DeleteSvg} style={{ color: 'rgba(0, 0, 0, 0.88)' }} />,
        description: (
          <div style={{ margin: '8px 22px 0 0' }}>{renderDeployedStatusDeletePrompt()}</div>
        ),
        okText: isDeployedStatus && !workbenchMode ? 'Undeploy & Delete' : 'Delete',
        okButtonProps: { danger: true },
      });
    }
    return deleteButtonProps;
  }, [
    handleDelete,
    isDeleteLoading,
    isDeployedStatus,
    isHasRelatedMetrics,
    isLoadingDeploy,
    isLoadingUndeploy,
    metricData,
    workbenchMode,
  ]);
  const deployButtonStatus = useMemo(() => {
    const action = isDeployedStatus ? 'Undeploy' : 'Deploy';
    const canAction = isDeployedStatus ? !isHasRelatedMetrics : true;
    const undeployButtonProps = {
      key: 'metric_detail_deploy_button',
      text: action,
      canAction,
      onConfirm: isDeployedStatus ? handleUndeploy : handleDeploy,
      title: canAction
        ? `${action} ${metricData?.name}?`
        : getRelatedConfirmTitle(metricData, action.toLowerCase()),
      button: (
        <Button
          loading={isLoading}
          disabled={isDeleteLoading}
          icon={<Icon component={isDeployedStatus ? UndeploySvg : DeploySvg} />}
          type="primary"
          ghost
          danger={isDeployedStatus}
          size="small"
        >
          {action}
        </Button>
      ),
    };
    if (canAction) {
      Object.assign(undeployButtonProps, {
        icon: (
          <Icon
            component={isDeployedStatus ? UndeploySvg : DeploySvg}
            style={{ color: 'rgba(0, 0, 0, 0.88)' }}
          />
        ),
        description: (
          <div style={{ margin: '8px 22px 0 0' }}>
            {isDeployedStatus ? renderUndeployPrompt() : renderDeployPrompt()}
          </div>
        ),
        tooltip: `${action} from Pulse`,
        okButtonProps: { danger: isDeployedStatus },
      });
    }
    return undeployButtonProps;
  }, [
    handleDeploy,
    handleUndeploy,
    isDeleteLoading,
    isDeployedStatus,
    isHasRelatedMetrics,
    isLoading,
    metricData,
  ]);
  return (
    <Layout>
      <TopPanel>
        <Title>
          {'Defined Metric:'}
          <span style={{ width: '8px' }} />
          <MetricName>
            <AddEllipsisWithTooltip text={metricData?.name || ''} dataIndex="0" />
          </MetricName>
        </Title>
        <Divider>
          {hasCreatePermission && !workbenchModeTrueButNotWorkbenchAsset(metricData?.is_beta) && (
            <>
              <Button
                icon={<Icon component={EditSvg} />}
                type="primary"
                onClick={() => {
                  setWorkflowFormMode(EFormMode.EDIT);
                  navigateMetricBuilderPage(metricId);
                }}
                ghost
                size="small"
                disabled={isDeleteLoading || isLoading}
              >
                Edit
              </Button>
              <Button
                icon={<Icon component={CloneIcon} />}
                onClick={() => {
                  setWorkflowFormMode(EFormMode.CLONE);
                  navigateMetricBuilderPage(`new?clone_id=${metricId}`);
                }}
                type="primary"
                ghost
                size="small"
                disabled={isDeleteLoading || isLoading}
              >
                Clone
              </Button>
              {!workbenchMode && <ActionConfirmButton {...deployButtonStatus} />}
              <ActionConfirmButton {...deleteButtonProps} />
            </>
          )}
        </Divider>
      </TopPanel>
      <ContentWrapper>
        <MetricTopInfo metricId={Number(metricId)} type="metric" />
        {isLoading ? (
          <Spin spinning={isLoading} />
        ) : (
          <div style={{ display: 'flex', marginLeft: '16px' }}>
            <MetricForm metric={metricData} formProps={{ form: form }} mode="view" />
            {screenWidth < 1440 ? null : <div style={{ width: '35%' }} />}
          </div>
        )}
      </ContentWrapper>
    </Layout>
  );
};

export default MetricsDetail;
